import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import apiClient from '../utils/apiClient';
import { CallCode, POI } from '../types/index.ts';
import { useSocket } from './SocketContext';

interface ConfigContextType {
  callCodes: Record<string, CallCode>;
  pois: POI[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [callCodes, setCallCodes] = useState<Record<string, CallCode>>({});
  const [pois, setPOIs] = useState<POI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const socket = useSocket();

  const fetchConfig = async () => {
    try {
      setLoading(true);
      setError(null);

      const [callCodesResponse, poisResponse] = await Promise.all([
        apiClient.getCallCodes(),
        apiClient.getPOIs(),
      ]);

      if (callCodesResponse.success && callCodesResponse.data) {
        // Debug: Log the first few call codes to see their structure
        const firstFew = Object.entries(callCodesResponse.data).slice(0, 3);
        console.log('[ConfigContext] First few call codes from server:', firstFew);
        setCallCodes(callCodesResponse.data);
        console.log('[ConfigContext] Call codes loaded', callCodesResponse.cached ? '(cached)' : '(fresh)');
      }

      if (poisResponse.success && poisResponse.data) {
        setPOIs(poisResponse.data);
        console.log('[ConfigContext] POIs loaded', poisResponse.cached ? '(cached)' : '(fresh)');
      }

      // Handle partial failures
      if (!callCodesResponse.success && !poisResponse.success) {
        setError('Failed to load configuration data');
      } else if (!callCodesResponse.success) {
        setError('Failed to load call codes');
      } else if (!poisResponse.success) {
        setError('Failed to load POIs');
      }
    } catch (err) {
      console.error('Failed to load configuration:', err);
      setError('Failed to load configuration data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  // Listen for config updates from socket
  useEffect(() => {
    if (!socket) return;

    const handleConfigUpdate = (data: any) => {
      console.log('[ConfigContext] Received config update:', data);
      
      if (data.type === 'pois' || data.type === 'callCodes') {
        console.log('[ConfigContext] Refreshing config data due to', data.type, 'update');
        fetchConfig();
      }
    };

    socket.on('config-updated', handleConfigUpdate);

    return () => {
      socket.off('config-updated', handleConfigUpdate);
    };
  }, [socket]);

  return (
    <ConfigContext.Provider value={{ callCodes, pois, loading, error, refetch: fetchConfig }}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

